import { useAutoneTranslation } from '@autone/translations';
import { getLocale } from '@autone/utils';
import { formatDistanceToNowStrict } from 'date-fns';
import { type FC, type PropsWithChildren, type ReactNode } from 'react';

import { InfoIcon } from '../../../../icons';
import { datasetTranslationKeyMap } from '../data-freshness-popover/data-freshness-popover-content/utils/data-source-translation-key-map';
import {
  type DatasetsDataFreshnessStatus,
  type GeneratedDataFreshnessDataset,
  type UploadedDataFreshnessDataset,
} from '../DataFreshnessWidget.types';
import { formatDatasetAge } from '../utils';
import { dataFreshnessStatusIconMap } from '../utils/data-age-icon-map';

import { DataFreshnessAlert } from './DataFreshnessAlert';
import { DataSourceFreshnessCard } from './DataSourceFreshnessCard';

interface DataFreshnessSidesheetContentProps {
  uploadedDatasets: UploadedDataFreshnessDataset[];
  generatedDatasets: GeneratedDataFreshnessDataset[];
  dataFreshnessStatus: DatasetsDataFreshnessStatus;
}
export const DataFreshnessSidesheetContent: FC<
  DataFreshnessSidesheetContentProps
> = ({ uploadedDatasets, generatedDatasets, dataFreshnessStatus }) => {
  const { common, lang } = useAutoneTranslation();

  return (
    <div className="flex flex-col gap-4">
      <DataFreshnessAlert status={dataFreshnessStatus} />
      <DataGroup
        title={common(
          'data-age.global-widget.details-sidesheet.uploaded-data.title',
        )}
        subtitle={common(
          'data-age.global-widget.details-sidesheet.uploaded-data.subtitle',
        )}
      >
        {uploadedDatasets.map((dataset) => (
          <DataSourceFreshnessCard
            key={dataset.name}
            Icon={dataFreshnessStatusIconMap[dataset.status]}
            status={dataset.status}
            label={common(datasetTranslationKeyMap[dataset.name])}
            formattedAge={formatDatasetAge(dataset.ageDays, lang, common)}
            impactedModules={dataset.impacted_modules}
          />
        ))}
      </DataGroup>
      {generatedDatasets.length > 0 && (
        <DataGroup
          title={common(
            'data-age.global-widget.details-sidesheet.autone-generated-data.title',
          )}
          subtitle={common(
            'data-age.global-widget.details-sidesheet.autone-generated-data.subtitle',
          )}
        >
          {generatedDatasets.map((dataset) => (
            <DataSourceFreshnessCard
              key={dataset.name}
              Icon={InfoIcon}
              label={common(datasetTranslationKeyMap[dataset.name])}
              formattedAge={
                dataset.updated_at
                  ? formatDistanceToNowStrict(new Date(dataset.updated_at), {
                      addSuffix: true,
                      locale: getLocale(lang),
                    })
                  : common('na')
              }
              impactedModules={dataset.impacted_modules}
            />
          ))}
        </DataGroup>
      )}
    </div>
  );
};

interface DataGroupProps extends PropsWithChildren {
  title: ReactNode;
  subtitle: ReactNode;
}

const DataGroup: FC<DataGroupProps> = ({ title, subtitle, children }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col">
      <h2 className="font-medium leading-tight">{title}</h2>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
    </div>
    {children}
  </div>
);
